"use client";

import { useState } from "react";
import Image from "next/image";
import ReactCountryFlag from "react-country-flag";
import { formatViewerHours } from "@/app/utils/timeUtils";
import { Commentator } from "@/app/types";

interface CommentatorDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  commentator: Commentator | null;
  rank?: number;
}

export default function CommentatorDetailsModal({
  isOpen,
  onClose,
  commentator,
  rank
}: CommentatorDetailsModalProps) {
  if (!isOpen || !commentator) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-widertalk-navy via-widertalk-navy/95 to-widertalk-navy/90 text-white p-6 rounded-t-2xl">
                   {/* Animated background pattern */}
          <div className="absolute inset-0 opacity-10 overflow-hidden rounded-t-2xl pointer-events-none">
            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl from-white/20 to-transparent rounded-full transform translate-x-20 -translate-y-20"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-white/10 to-transparent rounded-full transform -translate-x-16 translate-y-16"></div>
          </div>

          <div className="relative flex items-start gap-5">
            {/* Enhanced Avatar Section */}
            <div className="relative flex-shrink-0">
              {/* Ranking badge */}
              {rank && (
                <div className={`
                  absolute -top-3 -left-3 z-10 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shadow-lg border-2 border-white
                  ${rank === 1
                    ? 'bg-yellow-500 text-black'
                    : rank === 2
                      ? 'bg-gray-400 text-white'
                      : rank === 3
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-600 text-white'
                  }
                `}>
                  {rank}
                </div>
              )}

              {/* Avatar container */}
              <div className="relative w-20 h-20 rounded-xl overflow-hidden border-3 border-white/30 shadow-xl">
                {commentator.avatarUrl && commentator.avatarUrl.trim() !== "" ? (
                  <Image
                    src={commentator.avatarUrl}
                    alt={commentator.displayName || commentator.publicHandle || "Commentator"}
                    width={80}
                    height={80}
                    className="object-cover w-full h-full"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center">
                    <Image
                      src="/profile.svg"
                      alt={commentator.displayName || commentator.publicHandle || "Commentator"}
                      width={40}
                      height={40}
                      className="opacity-70"
                    />
                  </div>
                )}
              </div>

              {/* Enhanced nationality flag */}
              {commentator.nationality && commentator.nationality.trim() !== "" && (
                <div className="absolute -bottom-2 -right-2 bg-white rounded-full p-2 shadow-lg border-3 border-white">
                  <ReactCountryFlag
                    countryCode={commentator.nationality}
                    svg
                    style={{
                      fontSize: "18px",
                      lineHeight: "18px",
                      borderRadius: "3px"
                    }}
                    aria-label={commentator.nationality}
                  />
                </div>
              )}
            </div>

            {/* Header Content */}
            <div className="flex-1 min-w-0">
              {/* Name and handle */}
              <div className="mb-3">
                <h2 className="font-bold text-xl leading-tight text-white">
                  {commentator.displayName && commentator.displayName.trim() !== ""
                    ? commentator.displayName
                    : commentator.publicHandle
                  }
                </h2>
                {commentator.displayName && commentator.displayName.trim() !== "" && (
                  <p className="text-white/80 font-medium">
                    @{commentator.publicHandle}
                  </p>
                )}
                {commentator.joinedTimestamp && (
                  <p className="text-white/60 text-sm mt-1">
                    Member since {new Date(commentator.joinedTimestamp).toLocaleDateString('en-US', {
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                )}
              </div>

              {/* Quick stats */}
              <div className="grid grid-cols-2 gap-3">
                {/* Total Hours */}
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <svg className="w-4 h-4 text-widertalk-lime" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-xs text-white/80 font-medium">Total Hours</span>
                  </div>
                  <div className="text-lg font-bold text-white">
                    {formatViewerHours(commentator.totalViewerHours)}
                  </div>
                </div>

                {/* Average Viewers */}
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <svg className="w-4 h-4 text-widertalk-lime" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    <span className="text-xs text-white/80 font-medium">Avg Viewer Count</span>
                  </div>
                  <div className="text-lg font-bold text-white">
                    {Math.round(commentator.avgViewerCount)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {/* Description */}
          {commentator.description && commentator.description.trim() !== "" ? (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-widertalk-navy mb-3 flex items-center gap-2">
                <svg className="w-5 h-5 text-widertalk-navy" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                About
              </h3>
              <p className="text-gray-700 leading-relaxed bg-gray-50 rounded-lg p-4">
                {commentator.description}
              </p>
            </div>
          ) : (
            <div className="mb-6 text-center py-8">
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <p className="text-gray-500">This commentator hasn't added a description yet.</p>
            </div>
          )}

          {/* Detailed Stats */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-widertalk-navy mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-widertalk-navy" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Statistics
            </h3>

            <div className="space-y-4">
              {/* Language */}
              {commentator.preferredLanguage && (
                <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-widertalk-navy/10 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-widertalk-navy" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                      </svg>
                    </div>
                    <span className="font-medium text-gray-700">Preferred Language</span>
                  </div>
                  <span className="text-widertalk-navy font-semibold uppercase">
                    {commentator.preferredLanguage}
                  </span>
                </div>
              )}

              {/* Total Viewer Hours */}
              <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-widertalk-lime/20 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-widertalk-navy" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <span className="font-medium text-gray-700">Total Viewer Hours</span>
                </div>
                <span className="text-widertalk-navy font-bold text-lg">
                  {formatViewerHours(commentator.totalViewerHours)}
                </span>
              </div>

              {/* Average Viewers */}
              <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </div>
                  <span className="font-medium text-gray-700">Average Viewer Count</span>
                </div>
                <span className="text-blue-600 font-bold text-lg">
                  {Math.round(commentator.avgViewerCount)}
                </span>
              </div>

              {rank && (
                <div className="flex items-center justify-between py-3 px-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border border-yellow-200">
                  <div className="flex items-center gap-3">
                    <div className={`
                      w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                      ${rank === 1
                        ? 'bg-yellow-500 text-black'
                        : rank === 2
                          ? 'bg-gray-400 text-white'
                          : rank === 3
                            ? 'bg-orange-600 text-white'
                            : 'bg-gray-600 text-white'
                      }
                    `}>
                      {rank}
                    </div>
                    <span className="font-medium text-gray-700">Current Ranking</span>
                  </div>
                  <span className="text-orange-600 font-bold text-lg">
                    #{rank}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="text-center">
            <p className="text-gray-500 text-sm mb-4">
              This commentator is part of the WiderTalk community
            </p>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-widertalk-navy text-white font-semibold rounded-lg hover:bg-widertalk-navy/90 transition-colors focus:outline-none focus:ring-2 focus:ring-widertalk-navy focus:ring-offset-2"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
